import {
    getStorage,
    ref,
    uploadBytesResumable,
    getDownloadURL,
} from "firebase/storage";
import {
    collection,
    getFirestore,
    serverTimestamp,
    doc,
    setDoc,
    getDocs,
    query,
    where,
} from "firebase/firestore";
import firebaseApp from "../../../firebase/credenciales";
import sendCustomEmail from "../../../functions/sendCustomEmail";
import { sendNotificationBatch } from "../../../functions/sendNotification";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const CACHE_USUARIOS_TTL_MS = 5 * 60 * 1000;
const cacheUsuariosPorEmpresa = new Map();

/**
 * Carga (y cachea) los usuarios de la empresa indexados por DNI.
 *
 * Antes, `notificarUsuario` ejecutaba una consulta `users` por CADA documento
 * enviado (patrón N+1). Con 200 boletas eso eran 200 lecturas extra; ahora es
 * UNA lectura por lote de envíos.
 */
async function cargarUsuariosPorEmpresa(ruc) {
    const clave = String(ruc || "").trim();
    if (!clave) return new Map();

    const cached = cacheUsuariosPorEmpresa.get(clave);
    if (cached && Date.now() - cached.timestamp < CACHE_USUARIOS_TTL_MS) {
        return cached.data;
    }

    const mapa = new Map();
    try {
        const ref = collection(db, "users");
        let snapshot = await getDocs(query(ref, where("rucEmpresa", "==", clave)));
        if (snapshot.empty) {
            snapshot = await getDocs(query(ref, where("ruc", "==", clave)));
        }

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const dni = String(data.dni || docSnap.id || "").trim();
            if (!dni) return;
            mapa.set(dni, { ...data, id: docSnap.id });
        });
    } catch (error) {
        console.warn("No se pudieron cargar los usuarios de la empresa:", error);
    }

    cacheUsuariosPorEmpresa.set(clave, { timestamp: Date.now(), data: mapa });
    return mapa;
}

/**
 * Registra el documento en Firestore.
 *
 * El ID se genera en el cliente para poder persistir el documento y su campo
 * `id` en UNA sola escritura (antes: `addDoc` + `updateDoc`, dos viajes).
 */
async function registrarDocumento(infoDocumento) {
    const docRef = doc(collection(db, "document"));
    await setDoc(docRef, {
        ...infoDocumento,
        id: docRef.id,
        enviado: true,
        fechaEnvio: serverTimestamp(),
    });
    return docRef;
}

/**
 * Corrige el estado de envío cuando la notificación no se pudo entregar.
 * Solo escribe en el camino de error (raro), por lo que no añade tráfico
 * al flujo normal.
 */
async function marcarEnvioFallido(docRef) {
    if (!docRef) return;
    try {
        await setDoc(docRef, { enviado: false, fechaEnvio: null }, { merge: true });
    } catch (error) {
        console.warn("No se pudo marcar el documento como no enviado:", error);
    }
}

// Función para obtener texto del periodo
function getPeriodoTexto(mes) {
    const meses = [
        "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
        "JULIO", "AGOSTO", "SETIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"
    ];
    return meses[parseInt(mes) - 1] || "";
}

// Función para obtener tipo de documento (normalizado)
function getTipoDocumentoLabel(tipo) {
    const tipos = {
        "Boletas de pago": "BOLETA DE PAGO",
        "CTS": "CTS",
        "Gratificaciones": "GRATIFICACIÓN",
        "Utilidades": "LIQUIDACIÓN DE UTILIDADES",
        "Contratos": "CONTRATOS",
        "Renta": "RENTA DE 5TA CATEGORIA",
        "Memorandums": "MEMORANDUMS",
        "Vacaciones": "VACACIONES",
        "Prestamos": "PRESTAMO",
        "Adelantos": "ADELANTO",
        "PoliticasLaborales": "POLITICA SALARIAL",
        "Ceses": "CESES Y LIQUIDACIONES",
        "Otros": "OTROS"
    };
    return tipos[tipo] || String(tipo || "").toUpperCase();
}

/**
 * Genera el cuerpo HTML del correo electrónico
 */
function generarCorreoBody(nombreCompleto, mes, ano, empresaData, tipoDocLabel, semana = "") {
    return `<p><b>Estimado(a) ${nombreCompleto},</b></p>
<p>Te hacemos entrega de tu <b>${tipoDocLabel}</b> correspondiente al período <b>${mes}/${ano}${semana ? " " + semana : ""
        }</b>, conforme a lo establecido por la normativa vigente.</p>
<p><b>Recomendamos visualizar tu documento desde la aplicación móvil <i>Mi Boleta</i>, disponible en:</b></p>
<ul>
  <li><a href="https://play.google.com/store/apps/details?id=pe.perubi.miboleta"><b>Google Play (Android)</b></a></li>
  <li><a href="https://apps.apple.com/us/app/mi-boleta/id6504302634"><b>App Store (iPhone)</b></a></li>
</ul>
<p>La aplicación ofrece acceso completo, seguro y una experiencia optimizada para consultar tus boletas y otros documentos laborales.</p>
<p><b>Opcionalmente</b>, puedes acceder a través de la versión web con funcionalidades limitadas. Solo debes ingresar tu correo electrónico y, como contraseña, tu número de DNI:</p>
<p><a href="https://miboleta.net.pe/app/"><b>Accede aquí a Mi Boleta Web</b></a></p>
<p><b>Importante:</b></p>
<ul>
  <li>Asegúrate de tener la versión más reciente de la aplicación para garantizar una correcta visualización.</li>
  <li>Si tienes problemas para acceder, te sugerimos cerrar sesión e iniciar nuevamente.</li>
</ul>
<p>Si tienes alguna duda adicional, por favor comunícate con el Área de Recursos Humanos de tu empresa.</p>
<p>Atentamente,</p>
<p><b>${empresaData.name}</b></p>
<p style="font-size: 0.9em; color: gray;"><i>*Este mensaje ha sido enviado automáticamente. Te agradeceremos no responder a este correo. Para consultas personalizadas, comunicate con el Área de Recursos Humanos.</i></p>`;
}

/**
 * Notifica al usuario (push y/o correo) usando el mapa de usuarios ya cargado.
 * No realiza ninguna consulta a Firestore.
 */
async function notificarUsuario(documentoData, empresaData, usuariosPorDni) {
    const { dni, periodo, ano, semana, tipo } = documentoData;
    const dniLimpio = String(dni || "").trim();

    const usuario = usuariosPorDni?.get(dniLimpio) || null;
    if (!usuario) {
        console.warn(`Usuario con DNI ${dni} no encontrado. No se enviará notificación.`);
        return {
            notificado: false,
            viaEmail: false,
            viaPush: false,
            error: "Usuario no encontrado",
        };
    }

    const tipoLabel = getTipoDocumentoLabel(tipo);
    const mesLabel = getPeriodoTexto(periodo);
    const nombres = String(usuario.name || "").trim();
    const primerNombre = nombres ? nombres.split(" ")[0] : "colaborador";

    let viaPush = false;
    let viaEmail = false;

    // 1. Notificación Push (In-App)
    if (empresaData.notificaciones) {
        const textNotificacion = `Estimado ${primerNombre}, tu ${tipoLabel.toLowerCase()} del periodo ${mesLabel} ${ano}${semana ? " " + semana : ""
            } ya está disponible.`;

        const notificacion = {
            dni: dni,
            agentId: "comunication-agent",
            data: {
                isUser: false,
                read: false,
                text: textNotificacion,
            },
        };

        try {
            await sendNotificationBatch([notificacion]);
            viaPush = true;
        } catch (error) {
            console.error("Error enviando notificación push:", error);
        }
    }

    // 2. Envío de Correo Electrónico
    const coleccionEmail = String(empresaData.coleccionEmail || "").trim();
    if (!coleccionEmail) {
        console.warn("La empresa no tiene configurada una colección de email.");
        return {
            notificado: viaPush,
            viaEmail: false,
            viaPush,
            error: viaPush ? undefined : "Sin configuración de email",
        };
    }

    const asunto = `${empresaData.name} - ${tipoLabel} - ${periodo}/${ano}${semana ? " " + semana : ""}`;
    const body = generarCorreoBody(
        `${usuario.name || ""} ${usuario.apellidos || ""}`.trim(),
        periodo,
        ano,
        empresaData,
        tipoLabel,
        semana
    );

    const resultado = await sendCustomEmail(
        usuario.email,
        "", // CC
        "miboleta@perubi.com", // BCC
        asunto,
        body,
        coleccionEmail
    );

    if (!resultado?.success) {
        console.error("No se pudo encolar el correo:", resultado?.error);
        return {
            notificado: viaPush,
            viaEmail: false,
            viaPush,
            error: resultado?.error || "Error al enviar el correo",
        };
    }

    viaEmail = true;
    return { notificado: viaPush || viaEmail, viaEmail, viaPush };
}

/**
 * Procesa un único documento: Sube, Registra y Notifica
 * @param {Blob} fileBlob - El archivo PDF
 * @param {Object} metadata - Datos: { dni, ano, mes, tipo, nombreArchivo, empresaData, semana }
 * @param {Function} onProgress - Callback para progreso (opcional)
 */
export async function procesarDocumentoIndividual(fileBlob, metadata, onProgress) {
    const { dni, ano, mes, tipo, nombreArchivo, empresaData, semana } = metadata;

    // 1. Subir a Firebase Storage
    // Añadimos un hash aleatorio para evitar colisiones si se sube varias veces
    const uniqueName = `${nombreArchivo.replace('.pdf', '')}_${Date.now()}.pdf`;
    const storageRef = ref(storage, `documentos/${ano}/${dni}/${uniqueName}`);

    const uploadTask = uploadBytesResumable(storageRef, fileBlob);

    return new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                if (onProgress) {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress(progress);
                }
            },
            (error) => {
                console.error("Error subiendo archivo:", error);
                reject(error);
            },
            async () => {
                try {
                    // 2. Obtener URL de descarga
                    const downloadUrl = await getDownloadURL(storageRef);

                    // 3. Crear registro en Firestore (una sola escritura)
                    const mesLetras = getPeriodoTexto(mes);
                    const tipoLabel = getTipoDocumentoLabel(tipo);
                    let titulo = `${tipoLabel} ${mesLetras} ${ano}`;
                    if (semana) {
                        titulo += " " + semana;
                    }

                    const infoDocumento = {
                        activo: true,
                        ano: ano,
                        contador: 0,
                        fechaDoc: serverTimestamp(),
                        fechaVista: "",
                        dni: dni,
                        link: downloadUrl,
                        nombre: titulo,
                        periodo: mes + "", // Asegurar string
                        tipo: tipo,
                        ruc: empresaData.ruc,
                        semana: semana || "",
                    };

                    const docRef = await registrarDocumento(infoDocumento);

                    // 4. Notificar al usuario (usuarios cargados una sola vez)
                    const usuariosPorDni = await cargarUsuariosPorEmpresa(empresaData.ruc);
                    const notificacionResult = await notificarUsuario({
                        dni, periodo: mes, ano, semana, tipo
                    }, empresaData, usuariosPorDni);

                    // 5. Si la notificación falló, se corrige la marca de envío.
                    if (!notificacionResult?.notificado) {
                        await marcarEnvioFallido(docRef);
                    }

                    resolve({
                        success: true,
                        docId: docRef.id,
                        downloadUrl,
                        notificacion: notificacionResult,
                    });

                } catch (error) {
                    console.error("Error en post-procesamiento:", error);
                    reject(error);
                }
            }
        );
    });
}

/** Limpia la caché de usuarios (usado en pruebas). */
export function limpiarCacheUsuarios() {
    cacheUsuariosPorEmpresa.clear();
}
